<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { getDomoProxyUrl } from '@/analytics/domo-api'
import ProgressSpinner from 'primevue/progressspinner'
import GbInlineMessage from '@/components/GbInlineMessage.vue'

interface Props {
  embedId: string
  embedName?: string
  filters?: Record<string, string>
  height?: string
}

const props = withDefaults(defineProps<Props>(), {
  filters: () => ({}),
  height: '10500px' // this is not an elegant solution, but it works for now
})

const proxyUrl = ref<string>('')
const loading = ref<boolean>(true)
const error = ref<string>('')
const iframeRef = ref<HTMLIFrameElement | null>(null)

function loadEmbed() {
  if (!props.embedId) {
    error.value = 'Embed ID is required'
    loading.value = false
    return
  }

  loading.value = true
  error.value = ''

  proxyUrl.value = getDomoProxyUrl(props.embedId, props.filters)
}

watch(() => [props.embedId, props.filters], () => {
  loadEmbed()
}, { deep: true })

onMounted(() => {
  loadEmbed()
})

function handleIframeLoad() {
  loading.value = false
}

function handleIframeError() {
  error.value = 'Unable to Load Dashboard'
  loading.value = false
}
</script>

<template>
  <div class="w-full">
    <div v-if="loading" class="flex items-center justify-center min-h-[300px]">
      <div class="text-center">
        <ProgressSpinner class="mb-4 h-5 w-5 stroke-primary-400" strokeWidth="4" />
        <p class="text-gray-600">Loading {{embedName || 'analytics'}}...</p>
      </div>
    </div>

    <div v-else-if="error" class="flex items-center justify-center min-h-[200px]">
      <GbInlineMessage gbTheme="red" class="mb-4">
        <template #icon></template>
        <div class="inline-flex items-center">
          <span class="material-icons-round text-red-300 mr-2 self-start" aria-hidden="true">warning_amber</span>
          <div class="space-y-1">
            <p class="font-bold text-base leading-6 ">{{ error }}</p>
            <p class="text-sm leading-5">We're having trouble loading the Analytics Dashboard. Please check your internet connection and refresh the page to try again.</p>
          </div>
        </div>
      </GbInlineMessage>
    </div>

    <iframe
      v-if="proxyUrl"
      v-show="!loading && !error"
      ref="iframeRef"
      :src="proxyUrl"
      :style="{ height: props.height }"
      class="w-full border-0 rounded-lg"
      title="Analytics Dashboard"
      allowfullscreen
      @load="handleIframeLoad"
      @error="handleIframeError"
    />
  </div>
</template>
